import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Select, MenuItem } from '@mui/material';
import usePagination from '@mui/material/usePagination';
import AddTransactionHistory from './AddTransactionHistory/AddTransactionHistory';
import './IndividualPayoutDetail.css';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

const StatusColor = {
  // Pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  Failed: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Failed' },
  Success: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Success' },
};

const IndividualPayoutDetail = ({ teacher, payout }) => {
  const filters = ['This Week', 'This Month', '6 Month', '1 Year'];
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [rows, setRows] = useState([]);
  const [currentPayoutRequestId, setCurrentPayoutRequestId] = useState(null);
  const [rbacMe, setRbacMe] = useState(null);

  const hasPermission = permission => {
    return (rbacMe?.permissions || []).includes(permission);
  };

  const canManageIndividualPayout = () => {
    return (
      hasPermission('manage_individual_payout') || (rbacMe?.roles || []).includes('super_admin')
    );
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        let cachedData = null;
        try {
          const cached = sessionStorage.getItem('rbac_admin_me');
          if (cached) {
            cachedData = JSON.parse(cached);
            setRbacMe(cachedData);
          }
        } catch (_) {}

        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);

        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!rbacMe) setRbacMe({ roles: [], permissions: [] });
      }
    };

    fetchCurrentUser();
  }, []);

  const computeDateRange = useCallback(() => {
    if (!selectedFilter) {
      return { start_date: undefined, end_date: undefined };
    }

    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start = null;
    if (selectedFilter === 'This Week') {
      start = new Date(end);
      start.setDate(end.getDate() - 7);
    } else if (selectedFilter === 'This Month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (selectedFilter === '6 Month') {
      start = new Date(end);
      start.setMonth(end.getMonth() - 6);
    } else if (selectedFilter === '1 Year') {
      start = new Date(end);
      start.setFullYear(end.getFullYear() - 1);
    }
    const fmt = d =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      start_date: start ? fmt(start) : undefined,
      end_date: fmt(end),
    };
  }, [selectedFilter]);

  const fetchRows = useCallback(async () => {
    if (!teacher?.id) return;
    try {
      const params = { page: currentPage, page_size: pageSize };
      const dr = computeDateRange();
      if (dr.start_date) params.start_date = dr.start_date;
      if (dr.end_date) params.end_date = dr.end_date;
      const { data } = await axiosInstance.get(`/admin/transactions/payout-history/${teacher.id}`, {
        params,
      });
      const toDisplayDate = iso => {
        if (!iso) return '';
        const d = new Date(iso);
        const day = String(d.getDate()).padStart(2, '0');
        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const mon = monthNames[d.getMonth()];
        const yr = d.getFullYear();
        return `${day} ${mon} ${yr}`;
      };
      const toFormDate = iso => {
        if (!iso) return '';
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
      };
      const mapped = Array.isArray(data?.results)
        ? data.results.map(r => ({
            id: r.id,
            transactionId: r.transaction_id ? String(r.transaction_id) : '',
            date: toDisplayDate(r.transaction_date),
            formDate: toFormDate(r.transaction_date),
            earning: r.total_amount,
            bank: r.bank_deduction,
            actual: r.actual_amount,
            status:
              r.status?.toLowerCase() === 'success'
                ? 'Success'
                : r.status?.toLowerCase() === 'failed'
                  ? 'Failed'
                  : r.status,
            failed_reason: r.failed_reason || '',
          }))
        : [];
      setRows(mapped);
      setTotalCount(Number.isFinite(data?.count) ? data.count : 0);
    } catch (e) {
      toast.error('Failed to load payout history');
      setRows([]);
      setTotalCount(0);
    }
  }, [teacher?.id, currentPage, pageSize, computeDateRange]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const StatusPill = ({ value }) => {
    const statusConfig = StatusColor[value];
    if (!statusConfig) {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontFamily: 'Poppins',
            color: '#666',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
          }}
        >
          {value || 'Unknown'}
        </span>
      );
    }

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '3px 10px',
          borderRadius: '999px',
          fontSize: '12px',
          fontFamily: 'Poppins',
          color: statusConfig.color,
          backgroundColor: statusConfig.bgcolor,
          border: `1px solid ${statusConfig.color}`,
        }}
      >
        {statusConfig.value}
      </span>
    );
  };

  const toggleFilter = label => {
    setSelectedFilter(prev => (prev === label ? null : label));
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = event => {
    setPageSize(event.target.value);
    setCurrentPage(1);
  };

  // Use MUI usePagination hook for consistency with other pages
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const { items: paginationItems } = usePagination({
    count: totalPages,
    page: currentPage,
    onChange: handlePageChange,
  });

  const handleAddClick = () => {
    if (!canManageIndividualPayout()) {
      toast.error("You don't have permission to Edit/Add payouts");
      return;
    }
    const norm = (payout?.status || '').toLowerCase();
    if (norm && norm !== 'pending') {
      toast.error('Only pending payout can be added.');
      return;
    }
    setEditingTransaction(null);
    setCurrentPayoutRequestId(payout?.id || null);
    setIsModalOpen(true);
  };

  const handleEditClick = transaction => {
    if (!canManageIndividualPayout()) {
      toast.error("You don't have permission to Edit/Add payouts");
      return;
    }
    const mapped = {
      id: transaction.transactionId || transaction.id,
      date: transaction.formDate || '',
      bank: transaction.bank,
      actual: transaction.actual,
      status: transaction.status,
      payoutAmountRequested: transaction.earning,
      failed_reason: transaction.failed_reason || '',
    };
    setEditingTransaction(mapped);
    setCurrentPayoutRequestId(transaction.id || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTransaction(null);
  };

  const handleSaveTransaction = async transactionData => {
    if (editingTransaction) {
      console.log('Editing transaction:', transactionData);
    } else {
      console.log('Adding new transaction:', transactionData);
    }
    handleCloseModal();
    await fetchRows();
  };

  return (
    <Box className="payoutid-container">
      <Box className="payoutid-header-row">
        <Typography className="payoutid-title">Payout History</Typography>
        <Box className="payoutid-actions">
          {filters.map(label => (
            <button
              key={label}
              type="button"
              className={`payoutid-filter-btn ${selectedFilter === label ? 'payoutid-filter-active' : ''}`}
              onClick={() => toggleFilter(label)}
            >
              {label}
            </button>
          ))}
          <Button className="payoutid-add-btn" onClick={handleAddClick}>
            + Add
          </Button>
        </Box>
      </Box>

      <Box className="payoutid-table-scroll-container">
        <Box className="payoutid-table-wrapper">
          <table className="payoutid-table">
            <thead>
              <tr>
                <th>Transactions ID</th>
                <th>Transactions Date</th>
                <th>Payout Amount Requested (Riyal)</th>
                <th>Bank Deduction (Riyal)</th>
                <th>Actual Amount (Riyal)</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="payoutid-row">
                  <td>{r.transactionId || '-'}</td>
                  <td>{r.date}</td>
                  <td>{r.earning}</td>
                  <td>{r.bank}</td>
                  <td>{r.actual}</td>
                  <td>
                    <StatusPill value={r.status} />
                  </td>
                  <td>
                    <a
                      className="payoutid-edit-link"
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        handleEditClick(r);
                      }}
                    >
                      Edit
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>

      <Box className="payoutid-pagination-container" sx={{ marginTop: '0 !important' }}>
        <Box className="payoutid-pagination-box">
          <Typography className="payoutid-pagination-text">Showing</Typography>
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="payoutid-pagination-select"
            sx={{
              height: '30px',
              minWidth: '60px',
              fontFamily: 'Poppins',
              fontSize: '12px',
              margin: '0 4px',
              '& .MuiSelect-select': {
                padding: '6px 24px 6px 8px',
                textAlign: 'center',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                color: '#000',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-icon': {
                right: '4px',
                color: '#666',
              },
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
          </Select>
          <Typography className="payoutid-pagination-text">of {totalCount} transactions</Typography>
        </Box>

        {paginationItems.length > 0 && (
          <nav>
            <ul className="payoutid-pagination-list">
              {paginationItems.map(
                ({ page: itemPage, type, selected, disabled, ...item }, index) => {
                  let children = null;
                  if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                    children = <span className="payoutid-pagination-ellipsis">…</span>;
                  } else if (type === 'page') {
                    children = (
                      <button
                        type="button"
                        className={`payoutid-pagination-btn ${selected ? 'payoutid-pagination-btn-selected' : ''}`}
                        {...item}
                      >
                        {itemPage}
                      </button>
                    );
                  } else {
                    children = (
                      <button
                        type="button"
                        className={`payoutid-pagination-btn ${disabled ? 'payoutid-pagination-btn-disabled' : ''}`}
                        disabled={disabled}
                        {...item}
                      >
                        {type === 'previous' ? 'Previous' : 'Next'}
                      </button>
                    );
                  }
                  return <li key={index}>{children}</li>;
                }
              )}
            </ul>
          </nav>
        )}
      </Box>

      <AddTransactionHistory
        open={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTransaction}
        transaction={editingTransaction}
        payoutRequestId={currentPayoutRequestId}
      />
    </Box>
  );
};

export default IndividualPayoutDetail;
